import { create } from 'zustand'
import {
  CATEGORIES,
  SECTIONS,
  getAllCategories,
  getCategoriesBySection as getCategoriesBySectionConfig,
  getCategory,
  getSectionsWithCategories as getSectionsWithCategoriesConfig
} from '../config/categories'
import { promptsApi, customCategoriesApi } from '../services/apiService'

// Categories are now static config + custom categories from database
// Custom prompts are synced from cloud
const useCategoryStore = create((set, get) => ({
  // Pre-populate with static categories
  categories: getAllCategories(),
  // Custom categories from database
  customCategories: [],
  // Custom prompts from cloud (categoryCode -> prompt data)
  customPrompts: {},
  loading: false,
  error: null,

  // Load categories, custom categories, and custom prompts from cloud
  loadCategories: async () => {
    set({ loading: true })
    try {
      // Note: Prompts are now managed in localStorage via AIPromptsPage
      // Only load custom categories from API
      const customCats = await customCategoriesApi.getAll().catch(() => [])
      set({
        categories: getAllCategories(),
        customCategories: customCats || [],
        customPrompts: {}, // No longer loading from API
        loading: false
      })
    } catch (error) {
      console.error('Failed to load categories:', error)
      // Still use static categories even if custom fail to load
      set({ categories: getAllCategories(), customCategories: [], loading: false })
    }
  },

  // Get all active categories (static + custom)
  getActiveCategories: () => {
    const { customCategories } = get()
    const staticCats = getAllCategories()
    // Add custom categories that belong to sections
    const customInSections = customCategories.filter(c => c.section)
    return [...staticCats, ...customInSections]
  },

  // Get all custom categories (for admin)
  getCustomCategories: () => {
    return get().customCategories
  },

  // Get standalone custom categories (no section assigned)
  getStandaloneCategories: () => {
    const { customCategories } = get()
    return customCategories.filter(c => !c.section)
  },

  // Get categories by section (D, E, F, G) including custom
  getCategoriesBySection: (section) => {
    const { customCategories } = get()
    const staticCats = getCategoriesBySectionConfig(section)
    const customInSection = customCategories
      .filter(c => c.section === section)
      .map(c => ({
        code: c.code,
        name: c.name,
        section: c.section,
        icon: c.icon,
        aiPrompt: c.aiPrompt,
        isCustom: true
      }))
    return [...staticCats, ...customInSection]
  },

  // Get all sections with their categories (including custom)
  getSectionsWithCategories: () => {
    const { customCategories } = get()
    const sections = getSectionsWithCategoriesConfig()

    // Add custom categories to their sections
    customCategories.forEach(custom => {
      if (custom.section && sections[custom.section]) {
        sections[custom.section].categories[custom.code] = custom.name
      }
    })

    return sections
  },

  // Get section info
  getSection: (code) => SECTIONS[code],

  // Get category by code (e.g., 'D1', 'E3', or custom codes like 'D_custom_1')
  getCategory: (code) => {
    // First check static categories
    const staticCat = getCategory(code)
    if (staticCat) return staticCat

    // Then check custom categories
    const { customCategories } = get()
    const customCat = customCategories.find(c => c.code === code)
    if (customCat) {
      return {
        code: customCat.code,
        name: customCat.name,
        section: customCat.section,
        icon: customCat.icon,
        aiPrompt: customCat.aiPrompt,
        isCustom: true
      }
    }

    return null
  },

  // Legacy: Get category by code (alias for components still using old name)
  getCategoryByCode: (code) => {
    return get().getCategory(code)
  },

  // Get effective prompt for a category (custom if set, otherwise default)
  getPrompt: (code) => {
    const { customPrompts } = get()
    const category = getCategory(code)
    if (!category) return null

    // If we have custom prompt data from cloud, use it
    if (customPrompts[code]) {
      return {
        prompt: customPrompts[code].prompt,
        isCustom: customPrompts[code].isCustom,
        defaultPrompt: customPrompts[code].defaultPrompt || category.aiPrompt
      }
    }

    // Otherwise use default from static config
    return {
      prompt: category.aiPrompt,
      isCustom: false,
      defaultPrompt: category.aiPrompt
    }
  },

  // Save custom prompt to cloud
  updatePrompt: async (code, prompt) => {
    set({ loading: true })
    try {
      const result = await promptsApi.save(code, prompt)

      // Update local state
      const { customPrompts } = get()
      set({
        customPrompts: {
          ...customPrompts,
          [code]: result
        },
        loading: false
      })
      return result
    } catch (error) {
      console.error('Failed to save prompt:', error)
      set({ loading: false, error: error.message })
      throw error
    }
  },

  // Reset prompt to default (delete custom from cloud)
  resetPrompt: async (code) => {
    set({ loading: true })
    try {
      const result = await promptsApi.reset(code)

      // Update local state
      const { customPrompts } = get()
      set({
        customPrompts: {
          ...customPrompts,
          [code]: result
        },
        loading: false
      })
      return result
    } catch (error) {
      console.error('Failed to reset prompt:', error)
      set({ loading: false, error: error.message })
      throw error
    }
  },

  // Clear error
  clearError: () => set({ error: null }),

  // === Custom Category CRUD ===

  // Add a new custom category
  addCategory: async (categoryData) => {
    set({ loading: true })
    try {
      const category = await customCategoriesApi.create(categoryData)
      const { customCategories } = get()
      set({
        customCategories: [...customCategories, category],
        loading: false
      })
      return category
    } catch (error) {
      console.error('Failed to add category:', error)
      set({ loading: false, error: error.message })
      throw error
    }
  },

  // Update a custom category
  updateCategory: async (id, updates) => {
    set({ loading: true })
    try {
      const category = await customCategoriesApi.update(id, updates)
      const { customCategories } = get()
      set({
        customCategories: customCategories.map(c => c.id === id ? category : c),
        loading: false
      })
      return category
    } catch (error) {
      console.error('Failed to update category:', error)
      set({ loading: false, error: error.message })
      throw error
    }
  },

  // Delete a custom category
  deleteCategory: async (id) => {
    set({ loading: true })
    try {
      await customCategoriesApi.delete(id)
      const { customCategories } = get()
      set({
        customCategories: customCategories.filter(c => c.id !== id),
        loading: false
      })
    } catch (error) {
      console.error('Failed to delete category:', error)
      set({ loading: false, error: error.message })
      throw error
    }
  }
}))

export default useCategoryStore
